import { Mail, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export type NewFirmEmptyVariant = 'not_connected' | 'none_yet' | 'error' | 'manual_only';

interface NewFirmEmptyStateProps {
  variant: NewFirmEmptyVariant;
  accountEmail?: string | null;
  onConnectGmail?: () => void;
  onAddManually?: () => void;
}

const COPY: Record<NewFirmEmptyVariant, { title: string; description: (email?: string | null) => string }> = {
  not_connected: {
    title: 'Connect Gmail to pull in your first claim',
    description: () =>
      'ClaimHive reads claim emails from carriers, adjusters and policyholders, creates the claim and fills in the details for you.',
  },
  none_yet: {
    title: 'No claims found in your inbox yet',
    description: (email) =>
      `${email ? `ClaimHive checked ${email} and ` : 'ClaimHive '}has not found a claim email yet. You can add a claim yourself now, or connect another inbox that gets your claim emails.`,
  },
  error: {
    title: 'We could not scan your inbox',
    description: () =>
      'Something went wrong while reading your inbox. Reconnect Gmail to try again, or add a claim yourself in the meantime.',
  },
  manual_only: {
    title: 'No claims yet',
    description: () => 'Add your first claim to start working it in ClaimHive.',
  },
};

export function NewFirmEmptyState({ variant, accountEmail, onConnectGmail, onAddManually }: NewFirmEmptyStateProps) {
  const copy = COPY[variant];
  const connectLabel = variant === 'not_connected' ? 'Connect Gmail' : variant === 'error' ? 'Reconnect Gmail' : 'Connect another inbox';
  const connectIsPrimary = variant === 'not_connected' || variant === 'error';
  const showConnect = variant !== 'manual_only' && onConnectGmail;

  return (
    <Card className="border-dashed" data-testid="new-firm-empty-state" data-variant={variant}>
      <CardHeader>
        <CardTitle className="text-lg">{copy.title}</CardTitle>
        <CardDescription>
          {variant === 'manual_only' && !onAddManually
            ? 'Ask a firm admin to add a claim or assign one to you.'
            : copy.description(accountEmail)}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 sm:flex-row">
        {showConnect && (
          <Button
            variant={connectIsPrimary ? 'default' : 'outline'}
            className="w-full sm:w-auto"
            onClick={onConnectGmail}
            data-testid="empty-connect-gmail"
          >
            <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
            {connectLabel}
          </Button>
        )}
        {onAddManually && (
          <Button
            variant={showConnect && connectIsPrimary ? 'outline' : 'default'}
            className="w-full sm:w-auto"
            onClick={onAddManually}
            data-testid="empty-add-claim"
          >
            <PenLine className="mr-2 h-4 w-4" aria-hidden="true" />
            Add a claim manually
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
